import React from 'react';
import { useTheme } from '../contexts/ThemeContext';

// Spinner Loader - For buttons and small loading states
export const SpinnerLoader: React.FC<{ size?: 'sm' | 'md' | 'lg'; color?: string }> = ({
  size = 'md',
  color
}) => {
  const { currentTheme } = useTheme();

  const sizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-6 w-6',
    lg: 'h-12 w-12'
  };

  return (
    <div
      className={`animate-spin rounded-full border-b-2 ${sizeClasses[size]}`}
      style={{ borderColor: color || currentTheme.colors.primary[600] }}
    />
  );
};

// Full Page Loader - For page loading
export const PageLoader: React.FC<{ message?: string }> = ({ message = 'Loading...' }) => {
  const { currentTheme } = useTheme();

  return (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="text-center">
        <SpinnerLoader size="lg" />
        <p className="mt-4 text-sm" style={{ color: currentTheme.colors.text.secondary }}>
          {message}
        </p>
      </div>
    </div>
  );
};

// Skeleton Card - For loading card layouts
export const SkeletonCard: React.FC = () => {
  const { currentTheme } = useTheme();

  return (
    <div
      className="p-4 rounded-lg border animate-pulse"
      style={{
        backgroundColor: currentTheme.colors.background.card,
        borderColor: currentTheme.colors.border.primary
      }}
    >
      <div
        className="h-4 rounded mb-4"
        style={{ backgroundColor: currentTheme.colors.background.hover }}
      />
      <div
        className="h-3 rounded w-2/3 mb-3"
        style={{ backgroundColor: currentTheme.colors.background.hover }}
      />
      <div
        className="h-3 rounded w-1/2"
        style={{ backgroundColor: currentTheme.colors.background.hover }}
      />
    </div>
  );
};

// Skeleton Table - For loading table layouts
export const SkeletonTable: React.FC<{ rows?: number; columns?: number }> = ({
  rows = 5,
  columns = 4
}) => {
  const { currentTheme } = useTheme();

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y" style={{ borderColor: currentTheme.colors.border.primary }}>
        <thead style={{ backgroundColor: currentTheme.colors.background.secondary }}>
          <tr>
            {[...Array(columns)].map((_, i) => (
              <th key={i} className="px-4 py-3">
                <div
                  className="h-3 rounded animate-pulse"
                  style={{ backgroundColor: currentTheme.colors.background.hover }}
                />
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y" style={{ borderColor: currentTheme.colors.border.primary }}>
          {[...Array(rows)].map((_, rowIndex) => (
            <tr key={rowIndex} style={{ backgroundColor: currentTheme.colors.background.card }}>
              {[...Array(columns)].map((_, colIndex) => (
                <td key={colIndex} className="px-4 py-3">
                  <div
                    className="h-3 rounded animate-pulse"
                    style={{ backgroundColor: currentTheme.colors.background.hover }}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Skeleton Grid - For grid layouts (like Items page)
export const SkeletonGrid: React.FC<{ items?: number; columns?: 1 | 2 | 3 | 4 }> = ({
  items = 6,
  columns = 3
}) => {
  const gridCols = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 md:grid-cols-2',
    3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
  };

  return (
    <div className={`grid ${gridCols[columns]} gap-4`}>
      {[...Array(items)].map((_, i) => (
        <SkeletonCard key={i} />
      ))}
    </div>
  );
};

// Button Loader - For loading buttons
export const ButtonLoader: React.FC<{ text?: string }> = ({ text = 'Loading...' }) => {
  return (
    <>
      <SpinnerLoader size="sm" color="#ffffff" />
      <span className="ml-2">{text}</span>
    </>
  );
};

// Inline Loader - For inline loading states
export const InlineLoader: React.FC<{ text?: string }> = ({ text = 'Loading...' }) => {
  const { currentTheme } = useTheme();

  return (
    <div className="flex items-center gap-2">
      <SpinnerLoader size="sm" />
      <span className="text-sm" style={{ color: currentTheme.colors.text.secondary }}>
        {text}
      </span>
    </div>
  );
};

// Empty State - For when there's no data
export const EmptyState: React.FC<{
  icon?: React.ReactNode;
  title: string;
  description?: string;
  action?: React.ReactNode;
}> = ({ icon, title, description, action }) => {
  const { currentTheme } = useTheme();

  return (
    <div className="text-center py-12">
      {icon && (
        <div className="flex justify-center mb-4">
          {icon}
        </div>
      )}
      <h3 className="text-lg font-medium mb-2" style={{ color: currentTheme.colors.text.primary }}>
        {title}
      </h3>
      {description && (
        <p className="text-sm mb-4" style={{ color: currentTheme.colors.text.secondary }}>
          {description}
        </p>
      )}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
};

// Skeleton List - For list layouts
export const SkeletonList: React.FC<{ items?: number }> = ({ items = 5 }) => {
  const { currentTheme } = useTheme();

  return (
    <div className="space-y-3">
      {[...Array(items)].map((_, i) => (
        <div
          key={i}
          className="flex items-center gap-3 p-3 rounded-lg border animate-pulse"
          style={{
            backgroundColor: currentTheme.colors.background.card,
            borderColor: currentTheme.colors.border.primary
          }}
        >
          <div
            className="h-10 w-10 rounded-full"
            style={{ backgroundColor: currentTheme.colors.background.hover }}
          />
          <div className="flex-1 space-y-2">
            <div
              className="h-3 rounded w-1/4"
              style={{ backgroundColor: currentTheme.colors.background.hover }}
            />
            <div
              className="h-2 rounded w-1/2"
              style={{ backgroundColor: currentTheme.colors.background.hover }}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

// Page Skeleton - Complete page loading with header
export const PageSkeleton: React.FC<{ type?: 'grid' | 'table' | 'list' }> = ({ type = 'table' }) => {
  const { currentTheme } = useTheme();

  return (
    <div className="p-4 sm:p-6 space-y-6">
      {/* Header Skeleton */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div className="space-y-2 mb-4 sm:mb-0">
          <div
            className="h-8 w-48 rounded animate-pulse"
            style={{ backgroundColor: currentTheme.colors.background.hover }}
          />
          <div
            className="h-4 w-64 rounded animate-pulse"
            style={{ backgroundColor: currentTheme.colors.background.hover }}
          />
        </div>
        <div
          className="h-10 w-32 rounded animate-pulse"
          style={{ backgroundColor: currentTheme.colors.background.hover }}
        />
      </div>

      {/* Filter Skeleton */}
      <div
        className="rounded-lg border p-4"
        style={{
          backgroundColor: currentTheme.colors.background.card,
          borderColor: currentTheme.colors.border.primary
        }}
      >
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div
              key={i}
              className="h-10 rounded animate-pulse"
              style={{ backgroundColor: currentTheme.colors.background.hover }}
            />
          ))}
        </div>
      </div>

      {/* Content Skeleton */}
      {type === 'grid' && <SkeletonGrid />}
      {type === 'table' && <SkeletonTable />}
      {type === 'list' && <SkeletonList />}
    </div>
  );
};
